// Captures the browser's `beforeinstallprompt` event at app startup, BEFORE
// React mounts. Chrome can fire this event during initial page load — earlier
// than any component effect can attach a listener — so a hook-only listener
// races against it and intermittently misses it (the event doesn't replay).
//
// This module is imported first in index.tsx so the listener is registered
// synchronously at load. The hook (useInstallPrompt) reads the stashed event
// and subscribes for any later firing.

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((fn) => fn());

const clearInstallPrompt = () => {
  deferredPrompt = null;
  notify();
};

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Suppress Chrome's default mini-infobar; the app drives install from a menu.
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    notify();
  });
  // Once installed, the prompt is spent — clear it so the button hides.
  window.addEventListener("appinstalled", clearInstallPrompt);
}

export const getInstallPrompt = () => deferredPrompt;

/** Subscribe to install-availability changes; returns an unsubscribe fn. */
export const subscribeInstallPrompt = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const promptInstall = async () => {
  if (!deferredPrompt) return;
  await deferredPrompt.prompt();
  clearInstallPrompt(); // a prompt event can only be used once
};
